import React, {useEffect, useState, useMemo, useCallback} from "react";
import {Button, Card} from "react-bootstrap";
import {Photo, User} from "../types/Types";
import {collection, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db} from "../../firebase/FirebaseProvider";
import {PHOTOS_LIMIT, logDataLoaded} from "../util/Util";
import PhotoRow from "../locations/profile/PhotoRow";
import PhotoTag, {getDisplayPhotoTag} from "../tag/PhotoTag";
import AuthCheck from "../auth/AuthCheck";
import PageTracking from "../auth/PageTracking";
import LabelTag from "../tag/LabelTag";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faCalendar, faHeart, faTags} from "@fortawesome/free-solid-svg-icons";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../shared/v2/V2Glass";
import V2PhotoRow from "../shared/v2/V2PhotoRow";
import "./PhotosViewer.css";

interface Props {
    currentUser: User | undefined
    usersMap: Map<string, User>
    likesMap: Map<string, boolean>
}

function PhotosViewer(props: Props) {
    const { tag } = useParams<{ tag: string }>();
    const [searchParams] = useSearchParams();
    const [photos, setPhotos] = useState<Array<Photo> | undefined>(undefined);
    const [sortType, setSortType] = useState<string>('date');
    
    const updatePhotoLikeCount = useCallback((photoId: string, adjustment: number) => {
        setPhotos(prevPhotos => {
            if (!prevPhotos) return prevPhotos;
            return prevPhotos.map(photo => {
                if (photo.id === photoId) {
                    return {
                        ...photo,
                        likeCount: (photo.likeCount || 0) + adjustment
                    };
                }
                return photo;
            });
        });
    }, []);
    
    // Cache filtered photos by tag to avoid re-filtering on every sort
    const filteredPhotosByTag = useMemo(() => {
        if(!photos) {
            return []
        }
        if(tag !== "all") {
            return photos.filter((photo) => photo.tags.map(getDisplayPhotoTag).includes(tag || ''));
        } else {
            return photos;
        }
    }, [photos, tag]);

    useEffect(() => {
        (async () => {
            await loadPhotos();
        })();
    }, []);

    useEffect(() => {
        const sortParam = searchParams.get('sort');
        if (sortParam === 'likes' || sortParam === 'date') {
            setSortType(sortParam);
        }
    }, [searchParams]);

    const loadPhotos = async() => {
        // Load photos
        let photos: Array<Photo> = [];
        const photosOnceCollection = query(collection(db, 'photos'),
            where('hidden', '==', false),
            orderBy('createdAt', 'desc'),
            limit(PHOTOS_LIMIT));
        const photosOnceCollectionSnapshot = await getDocs(photosOnceCollection);
        photosOnceCollectionSnapshot.forEach((doc) => {
            let tempPhoto = doc.data() as Photo;
            tempPhoto.id = doc.id;
            photos.push(tempPhoto);
        });
        logDataLoaded('all photos', photos.length);
        setPhotos(photos);
    };

    const handleSort = (newSortType: string) => {
        setSortType(newSortType);
    };

    // Sort only the already-filtered photos (much faster)
    const displayPhotos = useMemo(() => {
        // Create a shallow copy to avoid mutating the original array
        const photosToSort = [...filteredPhotosByTag];
        
        // Sort based on sortType
        if(sortType === 'likes') {
            return photosToSort.sort((a: Photo, b: Photo) => (b.likeCount || 0) - (a.likeCount || 0));
        } else {
            // Default sort by date (newest first)
            return photosToSort.sort((a: Photo, b: Photo) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        }
    }, [filteredPhotosByTag, sortType]);

    const isUiV2 = !!props.currentUser?.enableUiV2;
    const displayTag = tag === "all" || !tag ? "All photos" : tag;

    if (isUiV2 && props.currentUser) {
        return (
            <>
                <PageTracking />
                <AuthCheck />
                <V2GlassPanel className="photos-v2-toolbar">
                    <div className="photos-v2-toolbar-main">
                        <Link className="photos-v2-back-button" to="/hub/photos">
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Photos
                        </Link>
                        <V2GlassChip className="photos-v2-tag-chip">
                            <FontAwesomeIcon icon={faTags} />
                            {displayTag}
                        </V2GlassChip>
                        <V2GlassSurface className="photos-v2-stat-chip">
                            <span>Photos</span>
                            <b>{displayPhotos.length}</b>
                        </V2GlassSurface>
                        <div className="photos-v2-sort-controls">
                            <button
                                className={`photos-v2-sort-button${sortType === "date" ? " photos-v2-sort-button-active" : ""}`}
                                disabled={sortType === "date"}
                                type="button"
                                onClick={() => handleSort("date")}
                                aria-label="Sort photos by date"
                            >
                                <FontAwesomeIcon icon={faCalendar} />
                            </button>
                            <button
                                className={`photos-v2-sort-button${sortType === "likes" ? " photos-v2-sort-button-active" : ""}`}
                                disabled={sortType === "likes"}
                                type="button"
                                onClick={() => handleSort("likes")}
                                aria-label="Sort photos by likes"
                            >
                                <FontAwesomeIcon icon={faHeart} />
                            </button>
                        </div>
                    </div>
                </V2GlassPanel>
                <V2GlassPanel className="photos-v2-gallery v2-glass-list-panel">
                    {!photos && <div className="photos-v2-empty-list">Loading photos...</div>}
                    {photos && displayPhotos.length === 0 && <div className="photos-v2-empty-list">No photos yet</div>}
                    {photos && displayPhotos.length > 0 && (
                        <div className="v2-hub-photo-row-list">
                            {displayPhotos.map((photo: Photo, index: number) => (
                                <V2PhotoRow
                                    key={photo.id + index}
                                    currentUser={props.currentUser!}
                                    photo={photo}
                                    photoUser={props.usersMap.get(photo.userId)}
                                    likesMap={props.likesMap}
                                    locationPlacement="bottom"
                                    onLikeCountChange={updatePhotoLikeCount}
                                />
                            ))}
                        </div>
                    )}
                </V2GlassPanel>
            </>
        );
    }

    return (
        <>
            <PageTracking />
            <AuthCheck />
            <Card className={"mb-2"}>
                <div className={"card-alt-padding"}>
                    <Link to={"/hub/photos"}>
                        <div className={"d-inline-block"}>
                            <LabelTag/>
                        </div>
                    </Link>
                    <div className={"ml-1 d-inline-block"}>
                        <PhotoTag name={tag || ''} count={0} bold={true} />
                    </div>
                    <div className={"d-inline-block ml-auto"} style={{float: "right"}}>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={sortType === 'date'} 
                            onClick={() => handleSort("date")}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={sortType === 'likes'} 
                            onClick={() => handleSort("likes")}
                        >
                            <FontAwesomeIcon icon={faHeart} />
                        </Button>
                    </div>
                </div>
            </Card>
            <Card className={"mb-3"}>
                <div>
                    {!photos && <div className={'loading'}>
                        Loading photos...
                    </div>}
                    {(photos && displayPhotos.length === 0) &&<div className={'loading'}>
                        No photos yet
                    </div>}
                    <div className={"album"}>
                        {photos && displayPhotos
                            .map((photo: Photo, index: number) => (
                            props.currentUser && <PhotoRow
                                key={photo.id + index}
                                currentUser={props.currentUser}
                                photo={photo}
                                photoUser={props.usersMap.get(photo.userId)}
                                displayLocation={true}
                                displayEditButton={true}
                                likesMap={props.likesMap}
                                onLikeCountChange={updatePhotoLikeCount}
                            />
                        ))}
                    </div>
                </div>
            </Card>
        </>
    )
}

export default PhotosViewer;
